import React from 'react'
import { classNames } from 'src/lib/utils/helpers'

export type AppLogoSize = 'sm' | 'md' | 'lg' | 'xl'

interface AppLogoProps {
  src?: string
  name?: string
  size?: AppLogoSize
  contain?: boolean
  bare?: boolean
  hoverShadow?: boolean
  className?: string
}

const SIZE_TOKENS: Record<AppLogoSize, { box: string; radius: string; initial: string }> = {
  sm: { box: 'w-9 h-9', radius: 'rounded-lg', initial: 'text-text-xs-semibold' },
  md: { box: 'w-10 h-10', radius: 'rounded-xl', initial: 'text-[10px] font-semibold' },
  lg: { box: 'w-11 h-11', radius: 'rounded-xl', initial: 'text-text-xs-semibold' },
  xl: { box: 'w-16 h-16', radius: 'rounded-xl', initial: 'text-text-md-semibold' }
}

const AppLogo: React.FC<AppLogoProps> = ({
  src,
  name,
  size = 'md',
  contain = false,
  bare = false,
  hoverShadow = true,
  className
}) => {
  const { box, radius, initial } = SIZE_TOKENS[size]
  const hasLogo = Boolean(src)
  const fallbackInitial = (name?.charAt(0) || '?').toUpperCase()
  const altText = name ?? ''

  return (
    <div
      className={classNames(
        box,
        radius,
        'overflow-hidden flex items-center justify-center flex-shrink-0 transition-all duration-150',
        bare && hasLogo
          ? ''
          : classNames(
              hoverShadow
                ? 'group-hover:shadow-[0_0_0_3px_rgba(9,9,11,0.06),0_12px_18px_0_rgba(0,0,0,0.05),0_4px_6px_0_rgba(0,0,0,0.03)]'
                : ''
            ),
        className ?? ''
      )}
    >
      {src ? (
        <img
          src={src}
          alt={altText}
          className={classNames('w-full h-full', contain ? 'object-contain' : 'object-cover')}
        />
      ) : (
        <span className={classNames(initial, 'text-zinc-700')}>{fallbackInitial}</span>
      )}
    </div>
  )
}

export default AppLogo
